<template>
  <CFlex v-if="showNotification" align="center" justify="center" column class="c-paste-notification">
    <h2>{{ $t('clipboardPaste.title') }}</h2>
    <p class="mb-2">{{ $t('clipboardPaste.text') }}</p>
    <CButton @click.stop="handleConfirm">{{ $t('clipboardPaste.continue') }}</CButton>
  </CFlex>
  <div v-else @paste.prevent="handlePaste" contenteditable class="c-paste-contenteditable" />
</template>

<script setup lang="ts">
  import { onMounted, onUnmounted, ref } from 'vue';
  import CFlex from './CFlex.vue';
  import CButton from './buttons/CButton.vue';

  const props = defineProps<{
    // eslint-disable-next-line no-unused-vars
    onPaste: (files: Array<File>) => void;
  }>();

  const showNotification = ref(false);
  const isMac = navigator.userAgent.toLowerCase().indexOf('mac') >= 0;

  async function handlePaste() {
    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const clipboardItem of clipboardItems) {
        const imageType = clipboardItem.types.find(type => type.startsWith('image/'));
        if (!imageType) return;

        // eslint-disable-next-line no-await-in-loop
        const blob = await clipboardItem.getType(imageType);
        const filename = `${(new Date().toJSON().slice(0, 19))}.${imageType?.split('/')[1]}`;
        const file = new File([blob], filename, { type: imageType, lastModified: Date.now() });
        props.onPaste([file]);
      }
    } catch (err) {
      if (err instanceof Error) {
        // eslint-disable-next-line no-console
        console.warn(err.name, err.message);
      }
    }
  }

  async function handleKeyDown(e: KeyboardEvent) {
    const isKeyboardPasteCommand = e.key === 'v' && (isMac ? e.metaKey : e.ctrlKey);
    if (isKeyboardPasteCommand) {
      try {
        const permissionStatus = await navigator.permissions.query({ name: 'clipboard-read', allowWithoutGesture: false });

        if (permissionStatus.state === 'prompt') {
          showNotification.value = true;
        }
        if (permissionStatus.state === 'denied') {
          // eslint-disable-next-line no-console
          console.warn('Permission to Access to clipboard is denied! You need to manually change the website permission clipboard settings to fix this.');
        }
        if (permissionStatus.state === 'granted') {
          await handlePaste();
        }

        permissionStatus.onchange = () => showNotification.value = false;
      } catch (err) {
        if (err instanceof Error) {
          // eslint-disable-next-line no-console
          console.warn(err.name, err.message);
        }
      }
    }
  }

  async function handleConfirm() {
    showNotification.value = false;
    await handlePaste();
  }

  onMounted(() => document.addEventListener('keydown', handleKeyDown));
  onUnmounted(() => document.removeEventListener('keydown', handleKeyDown));
</script>

<style lang="scss">
.c-paste-contenteditable {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  color: transparent;

  // Display nothing inside this contenteditable element.
  * { display: none; }
}
.c-paste-notification {
  position: absolute;
  width: 100%;
  top: 0;
  bottom: 0;
  background: #fff;
}
</style>/